import { computed } from "vue";
import { message } from "ant-design-vue";
import { renderEntity } from "@/src/utils/utils.js";
import { toFetchUsers } from "@/src/utils/userUtils.js";
import {
  getCountriesByProvider,
  getCountryCode,
} from "@/src/utils/phoneNumberUtils.js";
import { paidStatuses } from "@/src/constants/billingConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { bannerComponentTypes } from "@/src/constants/bannerConstants.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  callCampaignStatuses,
  validationEntities,
  callLogStatuses,
  dialerTypes,
  dndStatus,
  settingValues,
  callDirections,
} from "@/src/constants/callConstants.js";
import {
  countryFlagMapping,
  allEmergencyNumbers,
  countryMapping,
} from "@/src/constants/phoneNumberConstants.js";
import router from "@/src/router";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import CallService from "@/src/api/CallService.js";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import telephonyDevice from "@/src/services/telephony/telephonyDevice";
import { deduplicateRelationships } from "@/src/utils/dynamicCRMViewUtils.js";

export const isCallActive = computed(
  () => store.getters["calls/outgoingCallActive"],
);

export const initializeAndMakeCall = async ({
  phoneNumber,
  callParams,
  entityDetails,
  campaignId = null,
  entityType = "contact",
  taskId = 0,
}) => {

  try {
    if (!import.meta.env.DEV && !paidStatuses.includes(store.getters["billing/status"])) {
      const shouldShowModal = await store.dispatch(
        "calls/setShowAddCreditModal",
        true,
      );
      if (shouldShowModal) {
        return false;
      }
    }

    if (store.getters["calls/isCallConnected"]) {
      message.error("Please end the ongoing call to place a new call");
      return false;
    }
    await store.dispatch("calls/refresh");

    await store.dispatch("telephony/selectProviderBySourceNumber", {
      sourceNumber: callParams.From,
    });

    if (entityDetails) {
      await store.dispatch("calls/setEntityDetails", entityDetails);
    }

    const validate = validatePhoneNumber(
      phoneNumber,
      validationEntities.NORMAL_CALL,
      true,
    );
    if (!validate.valid) {
      if (campaignId) {
        const payload = {
          campaign_id: Number(campaignId),
          entity_id: entityDetails?.id,
          status: "skipped",
        };

        await toUpdateCallCampaignLeadStatus(payload, phoneNumber);
      } else message.error(validate.error);
      return false;
    }

    if (phoneNumber && callParams.From) {
      const params = {
        To: phoneNumber,
        From: callParams.From,
        Id: entityDetails?.id,
        CampaignId: Number(campaignId),
        entityType: entityType,
        taskId: taskId,
        callerId: store.getters["workspace/phoneNumbers"]?.find(
          (number) => number.phone_number === callParams.From,
        )?.caller_id,
      };

      const call = await telephonyDevice.makeCall(params);
      if (call) {
        console.log("Call initiated successfully");

        if (campaignId) {
          await handleSkippedCalls();
        }

        await store.dispatch("calls/startOutgoingCall", {
          details: {
            name: phoneNumber,
            number: phoneNumber,
            code: "+1",
            From: callParams.From,
          },
          isCampaign: !!campaignId,
        });
        return true;
      }
    } else {
      console.error("Please select both 'To' and 'From' numbers");
      return false;
    }
    return false;
  } catch (error) {
    console.error("An error occurred while initializing call: ", error);
  }
};

export const initializeDevice = async (config, retryCount = 0) => {
  try {
    await telephonyDevice.initialize(config);
    return true;
  } catch (error) {
    console.error("Failed to initialize telephony device:", error);
    if (retryCount < 3) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return initializeDevice(config, retryCount + 1);
    }
    throw error;
  }
};

export const makeCall = async (params) => {
  return await telephonyDevice.makeCall(params);
};

export const answerIncomingCall = async () => {
  return await telephonyDevice.answerIncomingCall();
};

export const rejectIncomingCall = () => {
  return telephonyDevice.rejectIncomingCall();
};

export const hangupCall = () => {
  return telephonyDevice.hangupCall();
};

export const hangupIncomingCall = () => {
  const device = telephonyDevice.currentDevice;
  if (device && device.hangupIncomingCall) {
    return device.hangupIncomingCall();
  }
};

export const toColdTransfer = async (params) => {
  return await telephonyDevice.toColdTransfer(params);
};

export const toWarmTransfer = async (params) => {
  return await telephonyDevice.toWarmTransfer(params);
};

export const swapCalls = async () => {
  return await telephonyDevice.swapCalls();
};

export const mergeConference = async () => {
  return await telephonyDevice.mergeConference();
};

export const startCallRecording = async () => {
  return await telephonyDevice.startRecording();
};

export const stopCallRecording = async () => {
  return await telephonyDevice.stopRecording();
};

export const getVoicemails = async () => {
  return await telephonyDevice.getVoicemails();
};

export const purchasePhoneNumber = async (params) => {
  return await telephonyDevice.purchasePhoneNumber(params);
};

export const deletePhoneNumber = async (params) => {
  return await telephonyDevice.deletePhoneNumber(params);
};

export const toTransferCall = async (params) => {
  return await toColdTransfer(params);
};

export const toExecuteWarmTransfer = async (params) => {
  return await toWarmTransfer(params);
};

export const toSwapCallsInConference = async () => {
  return await swapCalls();
};

export const toMergeConference = async () => {
  return await mergeConference();
};

export const getDeviceStatus = () => {
  return telephonyDevice.getDeviceStatus();
};

export const getCurrentConnection = () => {
  return telephonyDevice.getCurrentConnection();
};

export const getIncomingConnection = () => {
  return telephonyDevice.getIncomingConnection();
};

export const toGetPhoneNumbers = async (
  filters = {},
  includeUserId = false,
) => {
  try {
    const params = {
      filters: filters,
    };

    const response = await CallService.getPhoneNumbers(params);
    if (response?.success) {
      const phoneNumbers = response?.phone_numbers?.map((number) => ({
        ...number,
        assigned_to: renderEntity(number?.user || number?.team),
        flag: countryFlagMapping[number?.country_code],
        name: number.phone_number_name || "--",
      }));

      if (includeUserId) {
        return {
          phoneNumbers,
          currentUserId: response?.current_user_id,
        };
      }

      await store.dispatch("workspace/setPhoneNumbers", phoneNumbers);

      return phoneNumbers;
    }
  } catch (error) {
    console.error("An error occurred while getting the phone numbers: ", error);
  }
};

export const toFetchPhoneNumberDetails = async (number) => {
  try {
    const params = {
      phone_number: number,
    };

    const response = await CallService.fetchPhoneNumberDetails(params);
    if (response?.success) {
      return response?.details;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching phone number details: ",
      error,
    );
    return null;
  }
};

export const toUpdateCallCampaign = async (id, payload) => {
  try {
    const response = await CallService.updateCallCampaign(id, payload);
    if (response?.success) {
      await store.dispatch(
        "callsCampaign/updateCampaignDetails",
        response?.campaign,
      );
      return true;
    }
  } catch (error) {
    console.error("An error occurred while updating call campaign: ", error);
    return false;
  }
};

export const moveToNextCallCampaignStep = async (id) => {
  try {
    return await CallService.nextCallCampaignStep(id);
  } catch (error) {
    console.error(
      "An error occurred while moving to next call campaign step: ",
      error,
    );
  }
};

export const toUpdateCallCampaignLeadStatus = async (payload, number) => {
  try {
    const response = await CallService.updateCallCampaignLeadStatus(payload);
    if (response?.success) {
      await store.dispatch("callsCampaign/incrementSkippedCalls");
      await store.dispatch("callsCampaign/setCallOngoing", false);

      eventBus.emit("campaign-next-step", true);
      await store.dispatch("callsCampaign/refreshCampaignDetails");
    }
  } catch (error) {
    console.error(
      "An occurred while updating call campaign lead status: ",
      error,
    );
  }
};

export const toGetCallCampaignLeadsList = async (id, params = {}) => {
  try {
    const response = await CallService.getCallCampaignLeadsList(id, params);
    if (response?.success) {
      return {
        total: response?.total,
        lead_list: response?.lead_list?.map((lead) => ({
          id: lead.lead?.id,
          name: `${lead?.lead?.first_name || ""} ${lead?.lead?.last_name || ""}`.trim() || "--",
          phone_number: lead?.lead?.phone_number,
          call_status: lead?.call_status,
          disposition: lead?.disposition || "",
          current_role: lead?.lead?.current_role,
          current_company: lead.lead?.current_company || "",
          timezone: lead.lead?.timezone || "",
          entity_type: lead?.entity_type,
        })),
      };
    }
  } catch (error) {
    console.error(
      "An error occurred while getting the call campaign leads list",
      error,
    );
  }
};

export const toFetchVoicemails = async () => {
  try {
    const response = await CallService.fetchVoicemails();
    if (response?.success) {
      return response?.voicemails;
    }
  } catch (error) {
    console.error("An error occurred while fetching voicemails: ", error);
  }
};

export const toGetCallCampaignById = async (id) => {
  try {
    if (
      store.getters["callsCampaign/isCurrentCampaign"] &&
      store.getters["callsCampaign/currentCampaignId"] !== Number(id)
    ) {
      message.error(
        "Pause/End the campaign in progress to start a new campaign.",
      );
      return;
    }

    const response = await CallService.getCallCampaignById(id);
    if (response?.success) {
      await store.dispatch(
        "callsCampaign/setIsRecordingEnabled",
        response?.campaign?.record_calls,
      );
      await store.dispatch(
        "callsCampaign/updateCampaignDetails",
        response?.campaign,
      );
      return response?.campaign;
    }
  } catch (error) {
    console.error(
      "An error occurred while getting the call campaign by id",
      error,
    );
  }
};

export const toGetActivityWithSSID = async (id) => {
  try {
    const response = await CallService.getActivityWithSSID(id);
    return response?.call;
  } catch (error) {
    console.error("An occurred while trying to get activity details: ", error);
  }
};

export const stripEmptyHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<p[^>]*>(\s*|<br\s*\/?>)*<\/p>/g, "")
    .replace(/(<br\s*\/?>|\s)*$/, "")
    .trim();
};

export const toAddCallNote = async (id, notes) => {
  try {
    if (!notes) {
      return false;
    }

    let trimmedNotes = notes.replace(/<p[^>]*>(\s*|<br\s*\/?>)*<\/p>/g, "");
    trimmedNotes = trimmedNotes.replace(/\\u003c/g, "<").replace(/\\u003e/g, ">");
    trimmedNotes = trimmedNotes.replace(/(<br\s*\/?>|\s)*$/, "").trim();

    if (!trimmedNotes) {
      return false;
    }

    const response = await updateCallData(id, { note: trimmedNotes });
    return response?.success || false;
  } catch (error) {
    console.error("An error occurred while adding call note: ", error);
    return false;
  }
};

export const toFetchConferenceDetails = async (id) => {
  try {
    const response = await CallService.fetchConferenceDetails(id);
    if (response?.success) {
      await store.dispatch(
        "conferenceCall/setParticipants",
        response?.participants,
      );
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching conference details: ",
      error,
    );
  }
};

export const addNewPhoneNumber = async (payload) => {
  try {
    const users = await toFetchUsers();
    const ownerID = users.find(
      (user) => user?.email === localStorage.getItem("userEmail"),
    )?.id;

    const response = await DashboardService.addLead({
      ...payload,
      owner_id: ownerID,
    });

    if (response?.success) {
      await store.dispatch("calls/setIncomingContactDetails", {
        ...response.lead,
        type: "lead",
      });
      return response;
    }
  } catch (error) {
    console.error("An error occurred while adding a new phone number:", error);
  }
};

export const isValidNumber = (number) => {
  if (!number || number.trim() === "") {
    return false;
  }
  const cleanNumber = number.replace(/\D/g, "");
  return cleanNumber.length > 0 && /[1-9]/.test(cleanNumber);
};

export const isEmergencyNumber = (number) => {
  try {
    const cleanNumber = number.replace(/\D/g, "");
    return allEmergencyNumbers.includes(cleanNumber);
  } catch (error) {
    console.error(
      "An error occurred while checking if it is an emergency number or not: ",
      error,
    );
    return false;
  }
};

export const isCompleteNumber = (number) => {
  return /^\+?[0-9]{6,15}$/.test(number);
};

export const hasCountryCode = (number) => {
  return /^\+[0-9]{6,15}$/.test(number);
};

export const validatePhoneNumber = (number, entity, check = false) => {
  try {
    if (entity === validationEntities.NORMAL_CALL) {
      if (!isValidNumber(number)) {
        return {
          valid: false,
          error: "Please add a valid number to place a call",
        };
      }

      if (isEmergencyNumber(number)) {
        return { valid: false, error: "Emergency calls cannot be placed" };
      }

      if (!isCompleteNumber(number)) {
        return { valid: false, error: "Phone Number is not complete" };
      }

      if (
        check &&
        getCountryCode(number) !== countryMapping.IN &&
        store.getters["telephony/getCurrentProvider"] ===
          telephonyProviders.plivo
      ) {
        return {
          valid: false,
          error: "International Numbers cannot be placed using Indian numbers",
        };
      }
    }

    if (entity === validationEntities.TRANSFER_CALL) {
      if (!hasCountryCode(number)) {
        return {
          valid: false,
          error: "Please enter a valid phone number with a country code.",
        };
      }
      if (!isCompleteNumber(number)) {
        return { valid: false, error: "Phone Number is not complete" };
      }
    }

    return { valid: true };
  } catch (error) {
    console.error("An error occurred while validating phone number: ", error);
    return { valid: false, error: "Validation error" };
  }
};

export const handleCampaignStatusUpdateUtils = async (status) => {
  try {
    if (status === callCampaignStatuses.IN_PROGRESS) {
      await store.dispatch("callsCampaign/processNextCall");
    } else {
      await handleSkippedCalls();
    }
  } catch (error) {
    console.error(
      "An error occurred while handling campaign status update utils: ",
      error,
    );
  }
};

export const handleSkippedCalls = async () => {
  const skippedCalls = store.getters["callsCampaign/skippedCallsCount"];
  if (skippedCalls > 0) {
    message.info(`${skippedCalls} invalid number(s) were skipped`);
    await store.dispatch("callsCampaign/resetSkippedCalls");
  }
};

export const ongoingCall = () => {
  return (
    store.getters["calls/outgoingCallActive"] ||
    store.getters["calls/incomingCallActive"]
  );
};

export const formatTranscriptTime = (seconds) => {
  try {
    if (!seconds && seconds !== 0) return "";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const formattedMinutes = String(minutes);
    const formattedSeconds = String(remainingSeconds).padStart(2, "0");

    return `${formattedMinutes}:${formattedSeconds}`;
  } catch (error) {
    console.error(
      "An error occurred while formatting transcript time: ",
      error,
    );
  }
};

export const redirectToLeadsOrContacts = async (data) => {
  try {
    if (!data.entity_id || !data.entity_type) return;

    if (store.getters["calls/dispositionDrawerOpen"]) {
      await store.dispatch("calls/setPendingCampaignRedirect", data);
      return;
    }

    await getRecipientDetails(data?.entity_id, data?.entity_type);

    await router.push({
      name: `${data.entity_type}-detail`,
      params: { id: data.entity_id },
    });
  } catch (error) {
    console.error("An error occurred while redirecting to context: ", error);
  }
};

export const showCallCampaignBanner = async () => {
  try {
    await store.dispatch("dynamicBanner/updateShowBanner", true);
    await store.dispatch(
      "dynamicBanner/updateBannerComponentType",
      bannerComponentTypes.CALL_CAMPAIGNS,
    );
  } catch (error) {
    console.error(
      "An error occurred while showing call campaign banner: ",
      error,
    );
  }
};

export const getRecipientDetails = async (id, type) => {
  try {
    let response = null;
    if (type === entityTypes.CONTACT) {
      response = await DashboardService.getContact(id);
      if (response?.success) {
        await store.dispatch("calls/setEntityDetails", response?.contact);
        await store.dispatch("calls/setContactDetails", {
          type: callDirections.OUTGOING,
          details: response?.contact,
        });
        return response?.contact;
      }
    } else if (type === entityTypes.LEAD) {
      response = await DashboardService.getLead(id);
      if (response?.success) {
        await store.dispatch("calls/setEntityDetails", response?.lead);
        await store.dispatch("calls/setContactDetails", {
          type: callDirections.OUTGOING,
          details: response?.lead,
        });
        return response?.lead;
      }
    }
  } catch (error) {
    console.error("An error occurred while getting recipient details: ", error);
  }
};

export const isPlivoEnabled = () => {
  try {
    return !!localStorage.getItem("plivo");
  } catch (error) {
    console.error(
      "An error occurred while checking if plivo is enabled or not: ",
      error,
    );
  }
};

export const isPlivoProvider = (code) => {
  try {
    return (
      getCountriesByProvider(telephonyProviders.plivo).includes(code) ||
      getCountriesByProvider(telephonyProviders.plivoInternational).includes(
        code,
      )
    );
  } catch (error) {
    console.error(
      "An error occurred while checking if plivo is provider: ",
      error,
    );
  }
};

export const fetchCallDispositions = async ({
  search = "",
  page,
  pageSize,
} = {}) => {
  try {
    if (!search && !page && !pageSize && store.getters["calls/dispositions"].length > 0) {
      return store.getters["calls/dispositions"];
    }

    page = page || 1;
    pageSize = pageSize || 40;

    const params = {
      page: page,
      page_size: pageSize,
    };

    if (search) {
      params.search = search;
    }

    const response = await CallService.fetchCallDispositions(params);
    if (response?.data) {
      return response.data || [];
    }
    return [];
  } catch (error) {
    console.error("Error fetching dispositions: ", error);
    return [];
  }
};

export const createCallDisposition = async (dispositionName) => {
  try {
    const payload = { disposition: dispositionName };
    const response = await CallService.createCallDisposition(payload);
    if (response?.success) {
      store.dispatch("calls/refreshDispositions");
      return response.disposition;
    }
    throw new Error("Failed to create disposition");
  } catch (error) {
    console.error("Error creating disposition: ", error);
    throw error;
  }
};

export const deleteCallDisposition = async (dispositionId) => {
  try {
    const response = await CallService.deleteCallDisposition(dispositionId);
    if (response?.success) {
      store.dispatch("calls/refreshDispositions");
      return response.message;
    }
    throw new Error("Failed to delete disposition");
  } catch (error) {
    console.error("Error deleting disposition: ", error);
    throw error;
  }
};

export const updateCallData = async (callSid, data) => {
  try {
    if (!callSid) {
      return { success: false, error: "Call SID is required" };
    }

    if (!data || (!data.disposition && !("note" in data))) {
      return { success: false, error: "At least one field required" };
    }

    const payload = {};

    if (data.disposition) {
      payload.disposition = data.disposition;
    }

    if ("note" in data) {
      payload.note = data.note;
    }

    if (Object.keys(payload).length === 0) {
      return { success: false, error: "No valid data to update" };
    }

    const response = await CallService.updateCallData(callSid, payload);
    if (response?.success) {
      return { success: true, data: response };
    }
    
    return { success: false, error: "Failed to update call data" };
  } catch (error) {
    console.error("An error occurred while updating call data: ", error);
    return { success: false, error: error.message || "Update failed" };
  }
};

export const updateCallDisposition = async (callSid, disposition) => {
  try {
    return await updateCallData(callSid, { disposition });
  } catch (error) {
    console.error("Error updating disposition: ", error);
    return { success: false, error: error };
  }
};

export const getCallSetting = async (settingKey) => {
  try {
    if (!settingKey) return false;
    const response = await CallService.getDialerSetting(settingKey);
    return response?.success ? response.setting_value === settingValues.TRUE : false;
  } catch (error) {
    console.error("Error fetching call setting: ", error);
    return false;
  }
};

export const setCallSetting = async (settingKey, enabled) => {
  if (!settingKey) return false;
  try {
    const settingValue = enabled ? settingValues.TRUE : settingValues.FALSE;
    const response = await CallService.updateDialerSetting(
      settingKey,
      settingValue,
    );
    return response?.success || false;
  } catch (error) {
    console.error("Error updating call setting: ", error);
    return false;
  }
};

export const addToDNDBlocklist = async (
  phoneNumber,
  entity = null,
  dndStatus = "do_not_call",
) => {
  try {
    const blockListPayload = {
      number: phoneNumber,
      dnd_status: dndStatus,
    };

    // Only add entity fields if entity is provided
    if (entity?.id && entity?.type) {
      blockListPayload.entity_id = entity.id;
      blockListPayload.entity_type = entity.type;
    }

    const response = await CallService.addToDNDBlocklist(blockListPayload);
    return response;
  } catch (error) {
    console.error("Error adding to DND blocklist:", error);
    return { success: false, error: error.message };
  }
};

export const removeFromDNCBlocklist = async (phoneNumber) => {
  try {
    const response = await CallService.removeFromDNDBlocklist(phoneNumber);
    return response;
  } catch (error) {
    console.error("Error removing from DNC blocklist:", error);
    return { success: false, error: error.message };
  }
};

export const checkEntityDNDStatus = async (entityId, entityType) => {
  try {
    const response = await CallService.checkEntityDNDStatus(
      entityId,
      entityType,
    );
    return response;
  } catch (error) {
    console.error("Error checking entity DNC status:", error);
    return null;
  }
};


export const checkPhoneNumberDNCStatus = async (phoneNumber) => {
  try {
    if (!phoneNumber) {
      return { isDNC: false };
    }

    const response = await CallService.getDNDList({
      page: 1,
      page_size: 20,
      search_query: phoneNumber,
    });

    if (!response?.data || !Array.isArray(response.data)) {
      return { isDNC: false };
    }

    const dndEntry = response.data.find(
      (entry) => entry.number === phoneNumber
    );

    return {
      isDNC: dndEntry?.dnd_status === dndStatus.DO_NOT_CALL,
      dndEntry: dndEntry || null,
    };
  } catch (error) {
    console.error("Error checking phone number DNC status:", error);
    return { isDNC: false };
  }
};

export const formatCallStatus = (status) => {
  if (!status) return "";
  const formatted = status.replace(/[-_]/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

export const getDisplayStatus = (callDetails) => {
  const status = callDetails?.status || callDetails?.call_status || "";

  if (status === callLogStatuses.FAILED && callDetails?.hangup_reason) {
    if(callDetails.hangup_reason.match(/Cause:.*\| Code:.*\| Source:.*/)) {
      return status;
    }

    const formattedReason = formatCallStatus(callDetails.hangup_reason);
    return `${status} (${formattedReason})`;
  }
  return status;
};

export const getCampaignCallSid = (
  localCampaignCallSid,
  campaignPickupCallSid,
) => {
  try {
    const campaignType = store.getters["callsCampaign/campaignType"];
    const isParallelCampaign = campaignType === dialerTypes.PARALLEL_DIALER;

    return isParallelCampaign ? campaignPickupCallSid : localCampaignCallSid;
  } catch (error) {
    console.error("Error getting campaign call SID:", error);
    return localCampaignCallSid;
  }
};

export const getFromPhoneNumber = async ({
  campaignDetails,
  toPhoneNumber,
}) => {
  try {
    if (
      campaignDetails?.campaign_type === "power" &&
      campaignDetails?.number_selection === "auto"
    ) {
      const localPresenceResponse = await CallService.getLocalPresenceNumber({
        phone_number_to_call: toPhoneNumber,
      });

      if (
        localPresenceResponse?.success &&
        localPresenceResponse?.data?.local_phone_number
      ) {
        return localPresenceResponse.data.local_phone_number;
      }
    }
  } catch (error) {
    console.error("Failed to get local presence number:", error);
  }

  return campaignDetails?.phone_number;
};

export const openDialpad = async () => {
  try {
    await store.dispatch("calls/setShowDial", true);
  } catch (error) {
    console.error("Error opening dialpad:", error);
  }
};

export const fetchCampaigns = async (params = {}, campaignType = null) => {
  try {
    const response = await CallService.fetchCallCampaigns({
      ...params,
      campaignType: campaignType
    });
    
    if (response?.success) {
      return {
        campaigns: response.campaigns || [],
        count: response.count || response.total || 0,
        success: true
      };
    }
    return {
      campaigns: [],
      count: 0,
      success: false
    };
  } catch (error) {
    console.error("Error fetching campaigns:", error);
    return {
      campaigns: [],
      count: 0,
      success: false
    };
  }
};

export const deleteCampaign = async (campaignId) => {
  try {
    if (!campaignId) {
      throw new Error("Campaign ID is required");
    }
    const response = await CallService.deleteCallCampaign(campaignId);
    return {
      success: response?.success || true,
      message: response?.message || "Campaign deleted successfully"
    };
  } catch (error) {
    console.error("Error deleting campaign:", error);
    throw error;
  }
};

export const fetchCITemplates = async (
  page = 1,
  pageSize = 20
) => {
  try {
    const response = await CallService.fetchCITemplates(
      page,
      pageSize
    );

    if (!response?.success) {
      throw new Error("Failed to fetch templates");
    }

    return {
      success: true,
      data: response.data || [],
      total: response.total || 0,
    };
  } catch (error) {
    console.error("Error fetching conversation intelligence templates:", error);
    throw error;
  }
};

export const fetchCITemplateSettings = async (
  templateId,
  page = 1,
  pageSize = 20
) => {
  try {
    if (!templateId) {
      throw new Error("Template ID is required");
    }

    const response = await CallService.fetchCITemplateSettings(
      templateId,
      page,
      pageSize
    );

    if (!response?.success) {
      throw new Error("Failed to fetch template settings");
    }

    return {
      success: true,
      data: response.data || [],
      total: response.total || 0,
      template: response.template || null,
    };
  } catch (error) {
    console.error("Error fetching conversation intelligence template settings:", error);
    throw error;
  }
};

export const updateCITemplate = async (
  templateId,
  templateName
) => {
  try {
    if (!templateId) {
      throw new Error("Template ID is required");
    }

    if (!templateName || !templateName.trim()) {
      throw new Error("Template name is required");
    }

    const payload = {
      template_name: templateName,
    };

    const response = await CallService.updateCITemplate(
      templateId,
      payload
    );

    if (!response?.success) {
      throw new Error("Failed to update template");
    }

    return {
      success: true,
      data: response.data || null,
    };
  } catch (error) {
    console.error("Error updating conversation intelligence template:", error);
    throw error;
  }
};


export const fetchDispositionsForFilters = async (filtersOptions) => {
  try {
    const dispositions = await fetchCallDispositions({ search: "" });

    if (!dispositions || !Array.isArray(dispositions)) {
      return;
    }

    const dispositionFilter = filtersOptions.find((f) => f.id === "disposition");
    if (!dispositionFilter) {
      return;
    }

    dispositionFilter.options = dispositions.map((d) => ({
      id: d.disposition || d,
      name: d.disposition || d,
    }));
  } catch (error) {
    console.error("Error fetching dispositions for filters:", error);
  }
};

export const fetchCallLogSchema = async (callLogData) => {
  try {
    if (!callLogData?.entity_type) {
      return null;
    }

    const entityType = callLogData.entity_type;
    const objectType =
      entityType === entityTypes.LEAD
        ? entityTypes.LEADS
        : entityTypes.CONTACTS;
    const response = await CRMService.fetchSchemaObject(objectType);

    if (!response?.success || !response?.schema) {
      return null;
    }

    const schema = response.schema;
    if (schema?.relationships) {
      const deduplicatedRelationships = deduplicateRelationships(
        schema.relationships
      );
      schema.relationships = deduplicatedRelationships;
    }

    return schema;
  } catch (error) {
    console.error("Error fetching call log schema:", error);
    return null;
  }
};

export const formatCallCampaignLeadsForSelection = (leads, selectedIds, unselectedIds = [], selectAll = false) => {
  try {
    if (!Array.isArray(selectedIds) && !selectAll) {
      return {
        selected_entities: [],
        unselected_entities: [],
        select_all: false,
      };
    }

    if (selectAll) {
      const unselectedIdSet = new Set(unselectedIds);
      const selectedEntities = [];
      const unselectedEntities = [];
      
      if (leads && Array.isArray(leads)) {
        leads.forEach(lead => {
          const entity = {
            id: lead.id,
            entity_type: lead.entity_type || entityTypes.LEAD,
          };
          
          if (unselectedIdSet.has(lead.id)) {
            unselectedEntities.push(entity);
          } else {
            selectedEntities.push(entity);
          }
        });
      }
      
      return {
        selected_entities: selectedEntities,
        unselected_entities: unselectedEntities,
        select_all: true,
      };
    }

    const selectedIdSet = new Set(selectedIds);
    const leadsMap = new Map();
    
    if (leads && Array.isArray(leads)) {
      leads.forEach(lead => {
        leadsMap.set(lead.id, lead);
      });
    }
    
    const selectedEntities = Array.from(selectedIdSet).map(id => {
      const lead = leadsMap.get(id);
      return {
        id: id,
        entity_type: lead?.entity_type || entityTypes.LEAD,
      };
    });
    
    return {
      selected_entities: selectedEntities,
      unselected_entities: [],
      select_all: false,
    };
  } catch (error) {
    console.error("Error formatting call campaign leads for selection:", error);
    return {
      selected_entities: [],
      unselected_entities: [],
      select_all: false,
    };
  }
};
export const shouldOpenPostCallForm = () => {
  try {
    const isPowerDialer = store.getters["callsCampaign/campaignType"] === dialerTypes.POWER_DIALER;
    const isPostCallFormAutoOpen = store.getters["dialerSettings/isPostCallFormAutoOpenEnabled"];
    const callOngoing = store.getters["callsCampaign/callOngoing"];
    const isCallConnected = store.getters["calls/isCallConnected"];

    if (!callOngoing) {
      return false;
    }

    if (isPowerDialer && isPostCallFormAutoOpen) {
      return true;
    }

    return isCallConnected;
  } catch (error) {
    console.error("Error in shouldOpenPostCallForm:", error);
    return false;
  }
};